<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, ConfirmAction } from '@drama/ui'

import { unpublishNovelPack } from '../../../api/novel/packs.js'
import { useNovelPageText } from '../novel-page-text.js'
import { listingState } from './pack-status.js'

const props = defineProps({
  // 作品列表的一行。
  pack: { type: Object, required: true },
})
// done 的参数是完成的动作：published 或 unpublished。
const emit = defineEmits(['done'])

const { t } = useI18n()
const { apiErrorLine } = useNovelPageText()
const tp = (key, params) => t(`visualGameAdmin.novelPacks.${key}`, params ?? {})
const state = computed(() => listingState(props.pack))
const reasonId = computed(() => `novel-pack-blocked-${props.pack.id}`)

/** 确认框把 action 抛出的 message 原样显示在框里，所以先把接口错误换成文案再抛。 */
async function run(request) {
  try {
    return await request(props.pack.id)
  } catch (requestError) {
    throw new Error(apiErrorLine(requestError), { cause: requestError })
  }
}
</script>

<template>
  <span v-if="state === 'publishable'">{{ t('novelReview.useQueue') }}</span>
  <span
    v-else-if="state === 'deleted'"
    class="pla__reason"
    :data-testid="`novel-pack-deleted-${pack.id}`"
  >{{ t('novelStudio.projects.deletedHint') }}</span>
  <ConfirmAction
    v-else-if="state === 'listed'"
    :label="tp('actions.unpublish')"
    :confirm-text="tp('unpublishConfirm')"
    :cancel-label="t('visualGameAdmin.common.cancel')"
    :ok-label="tp('actions.unpublish')"
    :pending-label="t('visualGameAdmin.common.working')"
    danger
    :action="() => run(unpublishNovelPack)"
    :data-testid="`novel-pack-unpublish-${pack.id}`"
    @done="emit('done', 'unpublished')"
  />
  <span
    v-else
    class="pla"
  >
    <Button
      v-if="state === 'invalid'"
      variant="secondary"
      size="sm"
      disabled
      :aria-describedby="reasonId"
      :data-testid="`novel-pack-publish-${pack.id}`"
    >
      {{ tp('actions.publish') }}
    </Button>
    <span
      :id="reasonId"
      class="pla__reason"
      :data-testid="`novel-pack-blocked-${pack.id}`"
    >{{ tp(`blocked.${state}`) }}</span>
  </span>
</template>

<style scoped>
.pla { display: inline-flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-xs); }
.pla__reason { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
</style>
